import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, MapPin, CheckCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = ({ services }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const budgetOptions = [
    "$1,000 - $3,000",
    "$3,000 - $5,000",
    "$5,000 - $10,000",
    "$10,000+",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitted(true);
        toast.success("¡Mensaje enviado!", {
          description: "Te contactaremos en menos de 24 horas.",
        });
      }
    } catch (error) {
      toast.error("Error al enviar", {
        description: "Por favor intenta nuevamente.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      className="py-24 md:py-32 cta-gradient relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#2979FF]/20 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-[#00E5FF]/15 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF] mb-4 block">
            Contacto
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4">
            ¿Listo para{" "}
            <span className="gradient-text">escalar tu negocio</span>?
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Cuéntanos tu proyecto y te contactaremos en menos de 24 horas con una propuesta personalizada
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-semibold text-white mb-6">
                Hablemos de tu próximo proyecto
              </h3>
              <p className="text-zinc-400 leading-relaxed">
                Ya sea que necesites una landing page, una app móvil completa o un sistema SaaS con IA, estamos listos para convertir tu visión en realidad.
              </p>
            </div>

            {/* Contact cards */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5">
                <div className="w-12 h-12 rounded-xl bg-[#2979FF]/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#2979FF]" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Teléfono</p>
                  <p className="text-white font-medium">+1 475-988-5714</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5">
                <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#00E5FF]" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Email</p>
                  <p className="text-white font-medium">klyvora7@gmail.com</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Ubicación</p>
                  <p className="text-white font-medium">Connecticut, Estados Unidos</p>
                </div>
              </div>
            </div>

            {/* Response time */}
            <div className="p-6 rounded-2xl bg-gradient-to-r from-[#2979FF]/10 to-[#00E5FF]/10 border border-white/5">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle className="w-5 h-5 text-[#00E5FF]" />
                <span className="text-white font-medium">Respuesta garantizada</span>
              </div>
              <p className="text-zinc-400 text-sm">
                Respondemos todas las consultas en menos de 24 horas hábiles
              </p>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            {submitted ? (
              <div className="p-12 rounded-3xl bg-[#0A0A0A] border border-white/5 text-center">
                <div className="w-20 h-20 rounded-full bg-[#00E5FF]/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#00E5FF]" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4">
                  ¡Mensaje Enviado!
                </h3>
                <p className="text-zinc-400 mb-6">
                  Gracias por contactarnos. Te responderemos muy pronto.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      name: "",
                      email: "",
                      phone: "",
                      company: "",
                      service: "",
                      budget: "",
                      message: "",
                    });
                  }}
                  className="text-[#00E5FF] hover:underline"
                  data-testid="contact-form-reset"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="p-8 rounded-3xl bg-[#0A0A0A] border border-white/5 space-y-6"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      Nombre *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Tu nombre"
                      className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#2979FF]"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      Email *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="tu@email.com"
                      className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#2979FF]"
                      data-testid="contact-input-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      Teléfono
                    </label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+52 55 1234 5678"
                      className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#2979FF]"
                      data-testid="contact-input-phone"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      Empresa
                    </label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Tu empresa"
                      className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#2979FF]"
                      data-testid="contact-input-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      Servicio *
                    </label>
                    <Select
                      onValueChange={(value) => handleSelectChange("service", value)}
                      required
                    >
                      <SelectTrigger
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="contact-select-service"
                      >
                        <SelectValue placeholder="Selecciona un servicio" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        {services.map((service) => (
                          <SelectItem
                            key={service.id}
                            value={service.id}
                            className="text-white hover:bg-white/10"
                          >
                            {service.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">
                      Presupuesto
                    </label>
                    <Select
                      onValueChange={(value) => handleSelectChange("budget", value)}
                    >
                      <SelectTrigger
                        className="bg-white/5 border-white/10 text-white"
                        data-testid="contact-select-budget"
                      >
                        <SelectValue placeholder="Selecciona rango" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        {budgetOptions.map((option) => (
                          <SelectItem
                            key={option}
                            value={option}
                            className="text-white hover:bg-white/10"
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">
                    Cuéntanos sobre tu proyecto *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Describe tu proyecto, objetivos y cualquier detalle relevante..."
                    rows={4}
                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#2979FF] resize-none"
                    data-testid="contact-textarea-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#2979FF] to-[#00E5FF] text-white py-4 rounded-full font-medium text-base flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(0,229,255,0.5)] hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Enviar Mensaje
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
